import get from 'lodash/get';
import set from 'lodash/set';
import map from 'lodash/map';
import head from 'lodash/head';
import values from 'lodash/values';
import isEmpty from 'lodash/isEmpty';
import SectionIds from './SectionIds';
import SectionTypes from './SectionTypes';
import ClickTypes from './ClickTypes';
import { TRACKING_RELATED_ADS_MODAL } from '../Serp/constants';
import { TRACK_CATEGORIES, TRACK_ACTIONS, TRACK_LABELS } from './events';
import { MAX_AD_PAGE_VISIBLE_RELATED_ADS } from '../AdPage/constants';
import { MIN_ORGANIC_ADS_COUNT_TO_SHOW_SIMILAR_CONTENT_ADS } from './constants';
import { searchParametersSelector } from '~/Serp/selectors';

export const getEventSection = action =>
  action.data && action.data.section ? { section: action.data.section } : null;

export const getEventAdId = action =>
  action.data && action.data.adId ? { adId: action.data.adId } : null;

export const getTrackingCode = (origin, codes) => {
  const code = codes[String(origin)];
  return code ? { code } : null;
};

export const getNumPictures = pictures => (pictures ? pictures.length : 0);

export const getFormattedLocation = ad => {
  const location = ad.location;
  const region = location.region || '';
  const city = location.city || '';
  const cityArea = location.cityArea || '';
  const postalCode = ad.postCode || location.postalCode || '';

  const locationObject = {
    region: region.length !== 0 && { name: region },
    city: city.length !== 0 && { name: city },
    city_area: cityArea.length !== 0 && { name: cityArea },
    post_code: postalCode.length !== 0 && { value: postalCode },
  };

  // remove keys with false values
  Object.keys(locationObject).forEach(
    key => locationObject[key] === false && delete locationObject[key]
  );

  return { location: locationObject };
};

export const getABTestData = abTest => (abTest && !isEmpty(abTest) ? { ab_test: abTest } : null);

// since we need to track SECTION_SEARCH_FEWRESULTS and SECTION_PAID_SEARCH_FEWRESULTS (similar content ads organic/paid)
// even when the serp list doesn't have any similar content ad but the organic ads count is less than 15 results,
// we need to set this section to the adsGroupedBySectionId map to be tracked
export const addEmptyFewResultsSectionToAdsGroupedBySectionId = (
  nextState,
  adsGroupedBySection,
  sectionId
) => {
  const totalCount = nextState.serp.count;
  const similarContentAdsCount = nextState.serp.similarContentAdsCount;
  const shouldHaveSimilarContentAds =
    totalCount > 0 && totalCount <= MIN_ORGANIC_ADS_COUNT_TO_SHOW_SIMILAR_CONTENT_ADS;

  if (shouldHaveSimilarContentAds && similarContentAdsCount === 0) {
    return set(adsGroupedBySection, sectionId, [
      {
        section_id: sectionId,
        section_type_id: SectionTypes.SECTION_TYPE_ORGANIC, // section type is always organic in similar content ads
      },
    ]);
  }

  return adsGroupedBySection;
};

export const formattedAdForAdImpressions = ad => {
  const campaignIds = get(ad, ['source', 'ad', 'campaignIds']);
  const cpcDesktop = get(ad, ['source', 'ad', 'cpcDesktop']);
  const cpcMobile = get(ad, ['source', 'ad', 'cpcMobile']);

  const formattedAd = {
    id: get(ad, 'id'),
    source_id: get(ad, ['source', 'id']),
    position: get(ad, 'position'),
    date_time: get(ad, 'date'),
    cpc_desktop_list: cpcDesktop,
    cpc_mobile_list: cpcMobile,
    cpc_campaigns_list: campaignIds,
  };
  // Remove key with undefined values
  Object.keys(formattedAd).forEach(
    key => formattedAd[key] === undefined && delete formattedAd[key]
  );
  return formattedAd;
};

export const formattedSectionForAdImpressions = (section, nextState) => {
  const config = nextState.config;
  const trovit = nextState.trovit;
  const serp = nextState.serp;
  const searchParameters = searchParametersSelector(nextState);
  const firstSection = head(section);
  const firstSectionId = get(firstSection, 'section_id');

  const composeAdsArray = () => {
    const sectionId = firstSectionId;
    const similarContentAdsCount = get(serp, 'similarContentAdsCount');

    if (sectionId === SectionIds.SECTION_PAID_REGULARLANDING_ADS_NORESULTS) {
      return [];
    }

    // since we need to track when a few results section (similar content ads premium or organic) is printed
    // even if there are 0 similar content ads, we return empty array of ads of this section
    const isFewResultsSectionId =
      sectionId === SectionIds.SECTION_SEARCH_FEWRESULTS ||
      sectionId === SectionIds.SECTION_PAID_SEARCH_FEWRESULTS;
    if (isFewResultsSectionId && similarContentAdsCount === 0) {
      return [];
    }

    return values(map(section, ad => formattedAdForAdImpressions(ad)));
  };

  const event = {
    hitType: 'ad_impressions',
    eventMeta: {
      data: {
        type: 'ad_impressions',
        attributes: {
          section: {
            id: firstSectionId,
            type_id: get(firstSection, 'section_type_id'),
          },
          ads: composeAdsArray(),
          site: {
            country_code: config.countryCode,
            vertical_id: config.verticalId,
          },
          search: {
            what: get(searchParameters, 'what_d'),
            type_id: get(searchParameters, 'type_id'),
            order_by: 'default', // TODO: this is not sort, find out for what is this used for
            is_form: get(searchParameters, 'isSeoUrl') ? 0 : 1,
            total_results: get(serp, 'count'),
            page_number: get(searchParameters, 'page'),
            filters: {
              sort:
                get(searchParameters, 'sort') === '' ? 'relevance' : get(searchParameters, 'sort'),
              ...get(searchParameters, 'filters'),
            },
          },
          user: {
            origin_id: trovit.origin,
            cookie_id: trovit.installationId,
            agent: trovit.userAgent,
            ip: trovit.userIp,
          },
          tracking: {
            page_view_id: trovit.pageviewId,
            ...getTrackingCode(trovit.origin, trovit.trackingCodes),
          },
          ...getABTestData(trovit.abTest),
        },
      },
    },
  };

  return event;
};

export const formatDataToImpression = (section, nextState) => {
  const config = nextState.config;
  const trovit = nextState.trovit;
  const serp = nextState.serp;
  const searchParameters = searchParametersSelector(nextState);

  const sectionTypeId = get(searchParameters, 'isSemUrl')
    ? SectionTypes.SECTION_TYPE_PREMIUM
    : SectionTypes.SECTION_TYPE_ORGANIC;

  const totalResults = get(serp, 'didYouMeanWhat') ? 0 : get(serp, 'count');

  return {
    data: {
      attributes: {
        section: {
          id: section,
          type_id: sectionTypeId,
        },
        ads: [],
        site: {
          country_code: config.countryCode,
          vertical_id: config.verticalId,
        },
        search: {
          what: get(serp, 'didYouMeanWhat') || get(searchParameters, 'what_d'),
          type_id: get(searchParameters, 'type_id'),
          order_by: 'default', // TODO: this is not sort, find out for what is this used for
          is_form: get(searchParameters, 'isSeoUrl') ? 0 : 1,
          total_results: totalResults,
          page_number: get(searchParameters, 'page'),
          filters: {
            sort:
              get(searchParameters, 'sort') === '' ? 'relevance' : get(searchParameters, 'sort'),
            ...get(searchParameters, 'filters'),
          },
        },
        user: {
          origin_id: trovit.origin,
          cookie_id: trovit.installationId,
          agent: trovit.userAgent,
          ip: trovit.userIp,
        },
        tracking: {
          page_view_id: trovit.pageviewId,
          ...getTrackingCode(trovit.origin, trovit.trackingCodes),
        },
        ...getABTestData(trovit.abTest),
      },
    },
  };
};

// Test AB for task PWA-1095 - More like this (Featured ad on top)
export const formatDataToFeaturedAdImpression = nextState => {
  const featuredAd = get(nextState, ['featuredAd', 'data']);
  if (!featuredAd) {
    return null;
  }

  const config = nextState.config;
  const trovit = nextState.trovit;
  const serp = nextState.serp;
  const searchParameters = searchParametersSelector(nextState);

  return {
    data: {
      attributes: {
        section: {
          id: SectionIds.SECTION_RETENTION_FEATURED,
          type_id: SectionTypes.SECTION_TYPE_ORGANIC,
        },
        ads: [formattedAdForAdImpressions(featuredAd)],
        site: {
          country_code: config.countryCode,
          vertical_id: config.verticalId,
        },
        search: {
          what: get(searchParameters, 'what_d'),
          type_id: get(searchParameters, 'type_id'),
          order_by: 'default', // TODO: this is not sort, find out for what is this used for
          is_form: get(searchParameters, 'isSeoUrl') ? 0 : 1,
          total_results: get(serp, 'count'),
          page_number: get(searchParameters, 'page'),
          filters: {
            sort:
              get(searchParameters, 'sort') === '' ? 'relevance' : get(searchParameters, 'sort'),
            ...get(searchParameters, 'filters'),
          },
        },
        user: {
          origin_id: trovit.origin,
          cookie_id: trovit.installationId,
          agent: trovit.userAgent,
          ip: trovit.userIp,
        },
        tracking: {
          page_view_id: trovit.pageviewId,
          ...getTrackingCode(trovit.origin, trovit.trackingCodes),
        },
        ...getABTestData(trovit.abTest),
      },
    },
  };
};

export const formatDataToAdImpression = nextState => {
  const ad = nextState.adPage.ad;
  if (!ad) {
    return null;
  }

  const config = nextState.config;
  const trovit = nextState.trovit;
  const serp = nextState.serp;
  const searchParameters = searchParametersSelector(nextState);

  return {
    data: {
      attributes: {
        section: {
          id: SectionIds.SECTION_ADPAGE_VISIT_SOURCE,
          type_id: SectionTypes.SECTION_TYPE_PREMIUM,
        },
        ads: [formattedAdForAdImpressions(ad)],
        site: {
          country_code: config.countryCode,
          vertical_id: config.verticalId,
        },
        search: {
          what: get(searchParameters, 'what_d'),
          type_id: get(searchParameters, 'type_id'),
          order_by: 'default', // TODO: this is not sort, find out for what is this used for
          is_form: get(searchParameters, 'isSeoUrl') ? 0 : 1,
          total_results: get(serp, 'count'),
          page_number: get(searchParameters, 'page'),
          filters: {
            sort:
              get(searchParameters, 'sort') === '' ? 'relevance' : get(searchParameters, 'sort'),
            ...get(searchParameters, 'filters'),
          },
        },
        user: {
          origin_id: trovit.origin,
          cookie_id: trovit.installationId,
          agent: trovit.userAgent,
          ip: trovit.userIp,
        },
        tracking: {
          page_view_id: trovit.pageviewId,
          ...getTrackingCode(trovit.origin, trovit.trackingCodes),
        },
        ...getABTestData(trovit.abTest),
      },
    },
  };
};

export const formatDataToRelatedAdsImpression = (nextState, isFromModal) => {
  const ads = nextState.adPage.relatedAds;
  if (!ads || ads.size === 0) {
    return null;
  }

  const config = nextState.config;
  const trovit = nextState.trovit;
  const serp = nextState.serp;
  const searchParameters = searchParametersSelector(nextState);

  function getSectionId() {
    if (isFromModal) {
      return get(searchParameters, 'isSemUrl')
        ? SectionIds.SECTION_PAID_SEARCH_DUPLICATE
        : SectionIds.SECTION_SEARCH_DUPLICATE;
    }

    return SectionIds.SECTION_ADPAGE_SIMILAR;
  }

  return {
    data: {
      attributes: {
        section: {
          id: getSectionId(),
          type_id: get(searchParameters, 'isSemUrl')
            ? SectionTypes.SECTION_TYPE_PREMIUM
            : SectionTypes.SECTION_TYPE_ORGANIC,
        },
        ads: isFromModal
          ? ads.map(ad => formattedAdForAdImpressions(ad))
          : // Since we receive more than 6 related ads from API,
            // we only want to track the impression of the first visible ones
            ads
              .map(
                (ad, index) =>
                  index < MAX_AD_PAGE_VISIBLE_RELATED_ADS && formattedAdForAdImpressions(ad)
              )
              .filter(ad => ad),
        site: {
          country_code: config.countryCode,
          vertical_id: config.verticalId,
        },
        search: {
          what: get(searchParameters, 'what_d'),
          type_id: get(searchParameters, 'type_id'),
          order_by: 'default', // TODO: this is not sort, find out for what is this used for
          is_form: get(searchParameters, 'isSeoUrl') ? 0 : 1,
          total_results: get(serp, 'count'),
          page_number: get(searchParameters, 'page'),
          filters: {
            sort:
              get(searchParameters, 'sort') === '' ? 'relevance' : get(searchParameters, 'sort'),
            ...get(searchParameters, 'filters'),
          },
        },
        user: {
          origin_id: trovit.origin,
          cookie_id: trovit.installationId,
          agent: trovit.userAgent,
          ip: trovit.userIp,
        },
        tracking: {
          page_view_id: trovit.pageviewId,
          ...getTrackingCode(trovit.origin, trovit.trackingCodes),
        },
        ...getABTestData(trovit.abTest),
      },
    },
  };
};

export const formattedDataForLeadImpressions = nextState => {
  const searchParameters = searchParametersSelector(nextState);
  const config = nextState.config;
  const trovit = nextState.trovit;
  const ad = nextState.adPage.ad;

  return {
    data: {
      type: 'lead-impressions',
      attributes: {
        site: {
          country_code: config.countryCode,
          vertical_id: config.verticalId,
        },
        section: {
          id: SectionIds.SECTION_ADPAGE_CONTACT_FORM,
        },
        ad: {
          id: ad.id,
          dealer_type_id: 1, // @todo: pending to listing api has this parameter to pass it
          source: {
            id: get(ad, ['source', 'id']),
            ad_id: get(ad, ['source', 'ad', 'id']),
          },
        },
        search: {
          type_id: get(searchParameters, 'type_id'),
          page_number: get(searchParameters, 'page'),
        },
        user: {
          origin_id: trovit.origin,
          cookie_id: trovit.installationId,
          agent: trovit.userAgent,
          ip: trovit.userIp,
        },
        tracking: {
          page_view_id: trovit.pageviewId,
          ...getTrackingCode(trovit.origin, trovit.trackingCodes),
        },
        ...getABTestData(trovit.abTest),
      },
    },
  };
};

export const formatRequestBrowserPermissions = (action, nextState) => ({
  data: {
    type: 'browser-permissions',
    attributes: {
      type: action.data.label,
      tracking: {
        page_view_id: nextState.trovit.pageviewId,
      },
    },
  },
});

export const formatRequestBrowserStatus = (action, nextState) => {
  const trovit = nextState.trovit;
  const config = nextState.config;
  const notifications = nextState.notifications;
  return {
    data: {
      type: 'browser-statuses',
      attributes: {
        status: action.data.label,
        site: {
          country_code: config.countryCode,
          vertical_id: config.verticalId,
        },
        notification: {
          search_id: action.data.searchId,
          user_id: action.data.infrastructureId || notifications.infrastructureId,
        },
        tracking: {
          ...getTrackingCode(trovit.origin, trovit.trackingCodes),
        },
        ...getABTestData(trovit.abTest),
      },
    },
  };
};

export const formatDataToNavigationAdPage = (action, nextState) => {
  const config = nextState.config;
  const trovit = nextState.trovit;
  const ad = nextState.adPage.ad;
  const serp = nextState.serp;
  const searchParameters = searchParametersSelector(nextState);
  const verticalProperties = ad.verticalProperties;

  const clickType =
    action.data.action === TRACK_ACTIONS.ADPAGE_CLICKIN
      ? ClickTypes.ADPAGE_CLICKIN
      : ClickTypes.ADPAGE_CLICKIN_FORM;

  return {
    data: {
      type: 'ad-clicks',
      attributes: {
        type_id: clickType,
        site: {
          country_code: config.countryCode,
          vertical_id: config.verticalId,
        },
        section: {
          id: SectionIds.SECTION_PYA_ADPAGE_PAGINATOR_MLT,
          type_id: SectionTypes.SECTION_TYPE_ORGANIC,
          is_premium: ad.premium,
        },
        ad: {
          id: ad.id,
          vertical_properties: {
            is_new: ad.fresh,
          },
          source: {
            id: get(ad, ['source', 'id']),
            ad_id: get(ad, ['source', 'ad', 'id']),
            dealer_type_id: 1, // @todo: pending to listing api has this parameter to pass it
            num_pictures: getNumPictures(verticalProperties.images),
          },
          ...getFormattedLocation(ad),
        },
        user: {
          origin_id: trovit.origin,
          cookie_id: trovit.installationId,
          agent: trovit.userAgent,
          ip: trovit.userIp,
        },
        tracking: {
          page_view_id: trovit.pageviewId,
          ...getTrackingCode(trovit.origin, trovit.trackingCodes),
        },
        search: {
          what: get(searchParameters, 'what_d'),
          type_id: get(searchParameters, 'type_id'),
          order_by: 'default', // TODO: this is not sort, find out for what is this used for
          is_form: get(searchParameters, 'isSeoUrl') ? 0 : 1,
          total_results: get(serp, 'count'),
          page_number: get(searchParameters, 'page'),
          filters: {
            sort:
              get(searchParameters, 'sort') === '' ? 'relevance' : get(searchParameters, 'sort'),
            ...get(searchParameters, 'filters'),
          },
        },
        ...getABTestData(trovit.abTest),
      },
    },
  };
};

export const getEventMetaData = (action, prevState, nextState) => {
  if (
    action.data &&
    action.data.category === TRACK_CATEGORIES.ADPAGE &&
    action.data.action === TRACK_ACTIONS.IMPRESSION &&
    action.data.label === TRACK_LABELS.VISIT_SOURCE
  ) {
    return { eventMeta: formatDataToAdImpression(nextState) };
  }

  // Test AB for task PWA-1095 - More like this (Featured ad on top)
  if (
    action.data &&
    action.data.category === TRACK_CATEGORIES.SEARCH &&
    action.data.action === TRACK_ACTIONS.IMPRESSION &&
    action.data.label === TRACK_LABELS.FEATURED_AD
  ) {
    return { eventMeta: formatDataToFeaturedAdImpression(nextState) };
  }

  if (
    action.data &&
    action.data.category === TRACK_CATEGORIES.ADPAGE &&
    action.data.action === TRACK_ACTIONS.IMPRESSION &&
    action.data.label === TRACK_LABELS.RELATED_ADS
  ) {
    return { eventMeta: formatDataToRelatedAdsImpression(nextState) };
  }

  if (action.type === TRACKING_RELATED_ADS_MODAL) {
    const isFromModal = true;
    return { eventMeta: formatDataToRelatedAdsImpression(nextState, isFromModal) };
  }

  if (
    action.data &&
    action.data.category === TRACK_CATEGORIES.ADPAGE &&
    action.data.action === TRACK_ACTIONS.IMPRESSION &&
    action.data.label === TRACK_LABELS.LEADS_FORM
  ) {
    return { eventMeta: formattedDataForLeadImpressions(nextState) };
  }

  const impressionLabels = [
    TRACK_LABELS.NOT_FOUND,
    TRACK_LABELS.NAVIGATOR,
    TRACK_LABELS.EXPIRED_AD,
  ];
  if (
    action.data &&
    action.data.action === TRACK_ACTIONS.IMPRESSION &&
    impressionLabels.indexOf(action.data.label) > -1
  ) {
    return { eventMeta: formatDataToImpression(action.data.section, nextState) };
  }

  if (
    action.data &&
    action.data.category === TRACK_CATEGORIES.NOTIFICATIONS &&
    action.data.action === TRACK_ACTIONS.NOTIFICATIONS_PERMISSIONS
  ) {
    return { eventMeta: formatRequestBrowserPermissions(action, nextState) };
  }

  if (
    action.data &&
    action.data.category === TRACK_CATEGORIES.NOTIFICATIONS &&
    action.data.action === TRACK_ACTIONS.NOTIFICATIONS_STATUSES
  ) {
    return { eventMeta: formatRequestBrowserStatus(action, nextState) };
  }

  if (
    action.data &&
    action.data.category === TRACK_CATEGORIES.CLICKOUT &&
    (action.data.action === TRACK_ACTIONS.ADPAGE_CLICKIN ||
      action.data.action === TRACK_ACTIONS.ADPAGE_CLICKIN_FORM) &&
    action.data.label === TRACK_LABELS.NAVIGATOR
  ) {
    return { eventMeta: formatDataToNavigationAdPage(action, nextState) };
  }

  return null;
};



// WEBPACK FOOTER //
// ./app/tracking/utils.js